import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Grid,
  Typography,
  Box,
  Chip,
  useTheme,
  CircularProgress,
  IconButton,
  Switch,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Psychology as AIIcon,
  Assignment as StandardIcon,
  Check as CheckIcon,
  AccountTree as GraphIcon,
} from '@material-ui/icons'
import ClearIcon from '@material-ui/icons/Clear'
import {
  StyledTextField,
  pageVariants,
  childVariants,
  ClinicalAnalysisContainer,
  ClinicalAnalysisCard,
  ClinicalAnalysisIcon,
  ClinicalAnalysisTitle,
  ClinicalAnalysisDescription,
  ClinicalAnalysisRequirement,
  ClinicalModeContainer,
  clinicalAnalysisVariants,
  clinicalCardVariants,
} from '../styles/StyledComponents'
import HPOFilterWrapper from 'src/components/HPOFilterWrapper'
import { getHPOFromClinicalHistory } from 'src/api/HPO_suggestion/hpo_suggestion'
import HPOClusterGraph from './HPOClusterGraph'
import { alpha } from '@material-ui/core/styles'

const StepClinicalData = ({
  runAI,
  onRunAIChange,
  hpos,
  onHposChange,
  diseases,
  onDiseasesChange,
  clinicalHistory,
  onClinicalHistoryChange,
  userGuidance,
  onUserGuidanceChange,
  clinicalAnalysisType,
  setClinicalAnalysisType,
  isOntFlow = false,
  isPacbioFlow = false,
  runCnv = false,
  onRunCnvChange,
  runSv = false,
  onRunSvChange,
  runMod = false,
  onRunModChange,
  methylationAvailable,
  aiDisabled = false,
  aiDisabledReason = null,
}) => {
  const theme = useTheme()
  const [loadingHPO, setLoadingHPO] = useState(false)
  const [hpoGraph, setHpoGraph] = useState(null)
  const [graphOpen, setGraphOpen] = useState(false)
  // The raw AI suggestion list for the current clinical history. This follows the
  // clinical-history text; the visible chip list is derived from it below.
  const [originalSuggestions, setOriginalSuggestions] = useState([])
  const [rejectedHpoIds, setRejectedHpoIds] = useState([])
  const debounceTimerRef = useRef(null)
  const abortControllerRef = useRef(null)
  // Trimmed clinical-history text of the last successful fetch, so retyping the same
  // text (e.g. type a character then delete it) does not re-run the generation.
  const lastFetchedHistoryRef = useRef('')
  // Snapshot of the curation state (added terms + dismissals) taken when the graph
  // dialog opens, so "X"/backdrop/Esc/Cancel reverts the graph edits while
  // "Save & close" keeps them. The clinical-history text and its derived suggestions
  // are not part of this: they follow the text, which persists either way.
  const graphSnapshotRef = useRef(null)

  // The suggestion chips are derived, not stored: whatever the AI returned for the
  // current text, minus anything the user has already added or dismissed. Deriving it
  // keeps the chip list, the graph, and the selected terms in sync automatically, with
  // no imperative bookkeeping to drift out of date.
  const hpoSuggestions = useMemo(() => {
    const selectedIds = new Set((hpos?.hpoTerms || []).map((term) => term.hpo_id))
    const rejectedIds = new Set(rejectedHpoIds)
    return originalSuggestions
      .filter((hpoText) => {
        const hpId = hpoText.match(/HP:\d+/)?.[0]
        return hpId && !selectedIds.has(hpId) && !rejectedIds.has(hpId)
      })
      .slice(0, 99)
  }, [originalSuggestions, hpos, rejectedHpoIds])

  const aiCardVariants = {
    hidden: { opacity: 0, height: 0, y: -20 },
    visible: {
      opacity: 1,
      height: 'auto',
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      y: -20,
      transition: { duration: 0.3 },
    },
  }

  const handleClinicalAnalysisSelect = (type) => {
    if (type === 'ai-powered' && aiDisabled) {
      // AI analysis is not available for this file (e.g. CNV/SV VCF).
      return
    }
    setClinicalAnalysisType(type)
    if (type === 'standard') {
      onRunAIChange(false)
    } else if (type === 'ai-powered') {
      onRunAIChange(true)
    }
  }

  const fetchHPOs = async (clinicalHistoryText) => {
    if (!clinicalHistoryText || clinicalHistoryText.trim() === '') {
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoadingHPO(true)

    try {
      const response = await getHPOFromClinicalHistory(clinicalHistoryText)

      if (abortControllerRef.current?.signal.aborted) {
        return
      }

      if (response.success) {
        // Store the raw AI list; the visible chips are derived from it (see useMemo above).
        setOriginalSuggestions(response.hpo_names)
        setHpoGraph(response.graph || null)
        lastFetchedHistoryRef.current = clinicalHistoryText.trim()
      } else {
        const errorMsg = response.error || 'Failed to get HPO suggestions'
        console.error('[HPO Suggestion Error]', errorMsg)
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }
      const errorMsg = error.response?.data?.error || 'An error occurred while fetching HPO suggestions'
      console.error('[HPO Suggestion Error]', errorMsg, error)
    } finally {
      setLoadingHPO(false)
    }
  }

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (clinicalHistory && clinicalHistory.trim() !== '' && runAI && clinicalAnalysisType === 'ai-powered') {
      // Skip when the settled text matches the last successful fetch, so typing a
      // character and deleting it again does not spend another generation.
      if (clinicalHistory.trim() === lastFetchedHistoryRef.current) {
        return undefined
      }
      debounceTimerRef.current = setTimeout(() => {
        fetchHPOs(clinicalHistory)
      }, 800) // 800ms debounce delay
    } else if (!clinicalHistory || clinicalHistory.trim() === '') {
      // Clearing the text clears the AI-derived suggestions and graph only. Terms the
      // user already added or manually entered stay put; they are not tied to the text.
      setOriginalSuggestions([])
      setHpoGraph(null)
      lastFetchedHistoryRef.current = ''
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalHistory, runAI, clinicalAnalysisType])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Core accept/reject keyed by HP id and name, shared by the chip list (which parses
  // the id/name out of the doc string) and the cluster graph (which has them directly).
  const acceptHpoById = (hpId, name) => {
    if (!hpId) return
    const label = name || hpId
    const newHpoTerm = {
      hpo_id: hpId,
      name: label,
      value: hpId,
      label: `${label} (${hpId})`,
      synonyms: [],
    }

    const currentHpoTerms = hpos?.hpoTerms || []
    const existingIds = currentHpoTerms.map((term) => term.hpo_id)

    if (!existingIds.includes(hpId)) {
      const newHpoTerms = [...currentHpoTerms, newHpoTerm]
      onHposChange({
        hpos: newHpoTerms.map((term) => term.hpo_id),
        hpoNames: newHpoTerms.map((term) => term.name),
        hpoTerms: newHpoTerms,
      })
    }

    // Adding clears any prior rejection of the same term. The chip list drops it
    // automatically because the derivation excludes selected terms.
    setRejectedHpoIds((prev) => prev.filter((id) => id !== hpId))
  }

  // Remove an already-added term (un-select), used by the graph's ✗ on "added" nodes.
  const removeHpoById = (hpId) => {
    if (!hpId) return
    const currentHpoTerms = hpos?.hpoTerms || []
    const newHpoTerms = currentHpoTerms.filter((term) => term.hpo_id !== hpId)
    if (newHpoTerms.length !== currentHpoTerms.length) {
      onHposChange({
        hpos: newHpoTerms.map((term) => term.hpo_id),
        hpoNames: newHpoTerms.map((term) => term.name),
        hpoTerms: newHpoTerms,
      })
    }
  }

  const rejectHpoById = (hpId) => {
    if (!hpId) return
    setRejectedHpoIds((prev) => (prev.includes(hpId) ? prev : [...prev, hpId]))
    // Rejecting also un-adds it, if it had been added. The chip list drops it
    // automatically because the derivation excludes rejected terms.
    removeHpoById(hpId)
  }

  // Restore a dismissed term back to a neutral (suggested) state in the graph.
  const restoreHpoById = (hpId) => {
    if (!hpId) return
    setRejectedHpoIds((prev) => prev.filter((id) => id !== hpId))
  }

  // Terms the user has added (rendered as "added" in the graph) and terms dismissed.
  const selectedGraphIds = useMemo(() => new Set((hpos?.hpoTerms || []).map((term) => term.hpo_id)), [hpos])
  const rejectedGraphIds = useMemo(() => new Set(rejectedHpoIds), [rejectedHpoIds])
  const selectedCount = selectedGraphIds.size

  // Capture only the curation state so Cancel can restore it. The suggestion chips and
  // graph are derived from the clinical-history text, which persists regardless, so they
  // are intentionally left out: after a Cancel they reflect the current text, not a stale
  // snapshot taken before the text was edited inside the dialog.
  const openGraph = () => {
    graphSnapshotRef.current = { hpos, rejectedHpoIds }
    setGraphOpen(true)
  }

  const saveGraph = () => {
    graphSnapshotRef.current = null
    setGraphOpen(false)
  }

  const cancelGraph = () => {
    const snap = graphSnapshotRef.current
    if (snap) {
      onHposChange(snap.hpos || { hpos: [], hpoNames: [], hpoTerms: [] })
      setRejectedHpoIds(snap.rejectedHpoIds)
      graphSnapshotRef.current = null
    }
    setGraphOpen(false)
  }

  const handleAcceptHPO = (hpoText) => {
    const hpId = hpoText.match(/HP:\d+/)?.[0]
    const nameMatch = hpoText.match(/Name: ([^\n]+)/)
    const name = nameMatch ? nameMatch[1] : hpId

    acceptHpoById(hpId, name)
  }

  const handleRejectHPO = (hpoText) => {
    const hpId = hpoText.match(/HP:\d+/)?.[0]
    rejectHpoById(hpId)
  }

  // Compact suggestions UI (loading + chips + a "View graph" button that opens the
  // full cluster graph in a modal). Kept short so the right panel stays aligned.
  const renderHpoSuggestions = () => (
    <>
      {loadingHPO && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Analyzing clinical history...
          </Typography>
        </Box>
      )}
      {((hpoSuggestions && hpoSuggestions.length > 0) || (hpoGraph && hpoGraph.nodes && hpoGraph.nodes.length > 0)) && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Suggested HPO Terms:
            </Typography>
            {hpoGraph && hpoGraph.nodes && hpoGraph.nodes.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<GraphIcon sx={{ fontSize: '1rem' }} />}
                onClick={openGraph}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
              >
                View graph
              </Button>
            )}
          </Box>
          {hpoSuggestions && hpoSuggestions.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                maxHeight: '200px',
                overflowY: 'auto',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {hpoSuggestions.map((hpo, index) => {
                const hpId = hpo.match(/HP:\d+/)?.[0] || hpo
                const nameMatch = hpo.match(/Name: ([^\n]+)/)
                const name = nameMatch ? nameMatch[1] : hpId
                const chipLabel = name && name !== hpId ? `${name} (${hpId})` : hpId

                return (
                  <Chip
                    key={index}
                    label={chipLabel}
                    deleteIcon={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAcceptHPO(hpo)
                          }}
                          sx={{ padding: '2px', color: theme.palette.success.main }}
                        >
                          <CheckIcon sx={{ fontSize: '0.875rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRejectHPO(hpo)
                          }}
                          sx={{ padding: '2px', color: theme.palette.error.main }}
                        >
                          <ClearIcon sx={{ fontSize: '0.875rem' }} />
                        </IconButton>
                      </Box>
                    }
                    onDelete={() => {}}
                    sx={{
                      maxWidth: '300px',
                      '& .MuiChip-label': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                )
              })}
            </Box>
          )}
        </Box>
      )}
    </>
  )

  const handleHpoChange = (newHpoData) => {
    // Removing a term here brings it back into the suggestion chips automatically when it
    // was one of the AI's original suggestions, because the chip list is derived from
    // (original suggestions - selected - rejected). No manual re-append needed.
    onHposChange(newHpoData)
  }

  const renderClinicalAnalysisSelection = () => (
    <motion.div variants={clinicalAnalysisVariants} initial="hidden" animate="visible" exit="exit">
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
            mb: 1.5,
            fontSize: '1.4rem',
            letterSpacing: '0.5px',
          }}
        >
          Select Clinical Analysis Type
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Choose the level of clinical analysis and AI assistance for your genomic data interpretation.
        </Typography>
      </Box>

      <ClinicalAnalysisContainer>
        {/* Both cards share the wizard's full width equally, like the carrier panels. */}
        <ClinicalModeContainer>
          <motion.div
            variants={clinicalCardVariants}
            whileHover="hover"
            whileTap="tap"
            style={{ flex: 1, minWidth: 0 }}
          >
            <ClinicalAnalysisCard
              selected={clinicalAnalysisType === 'standard'}
              onClick={() => handleClinicalAnalysisSelect('standard')}
            >
              <ClinicalAnalysisIcon selected={clinicalAnalysisType === 'standard'}>
                <StandardIcon />
              </ClinicalAnalysisIcon>
              <ClinicalAnalysisTitle selected={clinicalAnalysisType === 'standard'}>
                Standard Clinical Analysis
              </ClinicalAnalysisTitle>
              <ClinicalAnalysisDescription>
                Basic clinical interpretation with manual review and standard annotation workflows.
              </ClinicalAnalysisDescription>
              <ClinicalAnalysisRequirement selected={clinicalAnalysisType === 'standard'}>
                Manual Review • Standard Annotation
              </ClinicalAnalysisRequirement>
            </ClinicalAnalysisCard>
          </motion.div>

          <Tooltip title={aiDisabled && aiDisabledReason ? aiDisabledReason : ''} placement="top" arrow>
            <motion.div
              variants={clinicalCardVariants}
              whileHover={aiDisabled ? undefined : 'hover'}
              whileTap={aiDisabled ? undefined : 'tap'}
              style={{ flex: 1, minWidth: 0 }}
            >
              <ClinicalAnalysisCard
                selected={clinicalAnalysisType === 'ai-powered'}
                onClick={() => handleClinicalAnalysisSelect('ai-powered')}
                sx={aiDisabled ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              >
                <ClinicalAnalysisIcon selected={clinicalAnalysisType === 'ai-powered'}>
                  <AIIcon />
                </ClinicalAnalysisIcon>
                <ClinicalAnalysisTitle selected={clinicalAnalysisType === 'ai-powered'}>
                  AI-Powered Analysis
                </ClinicalAnalysisTitle>
                <ClinicalAnalysisDescription>
                  {aiDisabled && aiDisabledReason
                    ? aiDisabledReason
                    : 'Advanced AI-driven variant prioritization with phenotype correlation and clinical insights.'}
                </ClinicalAnalysisDescription>
                <ClinicalAnalysisRequirement selected={clinicalAnalysisType === 'ai-powered'}>
                  AI Prioritization • Phenotype Correlation
                </ClinicalAnalysisRequirement>
              </ClinicalAnalysisCard>
            </motion.div>
          </Tooltip>
        </ClinicalModeContainer>
      </ClinicalAnalysisContainer>
    </motion.div>
  )

  const renderOntPipelineOptions = () => (
    <Grid item xs={12}>
      <motion.div variants={aiCardVariants} initial="hidden" animate="visible" exit="exit">
        <Box
          sx={{
            p: 2.5,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}>
            Pipeline Options
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block' }}>
            Select which analyses to run on the ONT BAM file. These are computationally expensive, so only enable what
            you need.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              onClick={() => onRunCnvChange && onRunCnvChange(!runCnv)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${runCnv ? alpha(theme.palette.success.main, 0.38) : theme.palette.divider}`,
                backgroundColor: runCnv ? alpha(theme.palette.success.main, 0.03) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.25),
                  backgroundColor: alpha(theme.palette.success.main, 0.02),
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  CNV Analysis (Spectre)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Detect Copy Number Variations using Spectre
                </Typography>
              </Box>
              <Switch
                checked={runCnv}
                onChange={(e) => onRunCnvChange && onRunCnvChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="default"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.success.main,
                    '& + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                  },
                }}
              />
            </Box>
            <Box
              onClick={() => onRunSvChange && onRunSvChange(!runSv)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${runSv ? alpha(theme.palette.success.main, 0.38) : theme.palette.divider}`,
                backgroundColor: runSv ? alpha(theme.palette.success.main, 0.03) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.25),
                  backgroundColor: alpha(theme.palette.success.main, 0.02),
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  SV Analysis (Sniffles2)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Detect Structural Variants using Sniffles2
                </Typography>
              </Box>
              <Switch
                checked={runSv}
                onChange={(e) => onRunSvChange && onRunSvChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="default"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.success.main,
                    '& + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                  },
                }}
              />
            </Box>
            <Box
              onClick={() => onRunModChange && onRunModChange(!runMod)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${runMod ? alpha(theme.palette.success.main, 0.38) : theme.palette.divider}`,
                backgroundColor: runMod ? alpha(theme.palette.success.main, 0.03) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.25),
                  backgroundColor: alpha(theme.palette.success.main, 0.02),
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Methylation Analysis (modkit)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Detect CpG methylation (5mC). Requires base modification data in the BAM file.
                </Typography>
              </Box>
              <Switch
                checked={runMod}
                onChange={(e) => onRunModChange && onRunModChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="default"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.success.main,
                    '& + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Grid>
  )

  const renderPacbioPipelineOptions = () => (
    <Grid item xs={12}>
      <motion.div variants={aiCardVariants} initial="hidden" animate="visible" exit="exit">
        <Box
          sx={{
            p: 2.5,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}>
            Pipeline Options
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block' }}>
            Select which analyses to run on the PacBio BAM file. These are computationally expensive, so only enable
            what you need.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              onClick={() => onRunCnvChange && onRunCnvChange(!runCnv)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${runCnv ? alpha(theme.palette.success.main, 0.38) : theme.palette.divider}`,
                backgroundColor: runCnv ? alpha(theme.palette.success.main, 0.03) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.25),
                  backgroundColor: alpha(theme.palette.success.main, 0.02),
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  CNV Analysis (HiFiCNV)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Detect Copy Number Variations using HiFiCNV
                </Typography>
              </Box>
              <Switch
                checked={runCnv}
                onChange={(e) => onRunCnvChange && onRunCnvChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="default"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.success.main,
                    '& + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                  },
                }}
              />
            </Box>
            <Box
              onClick={() => onRunSvChange && onRunSvChange(!runSv)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${runSv ? alpha(theme.palette.success.main, 0.38) : theme.palette.divider}`,
                backgroundColor: runSv ? alpha(theme.palette.success.main, 0.03) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.25),
                  backgroundColor: alpha(theme.palette.success.main, 0.02),
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  SV Analysis (pbsv)
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Detect Structural Variants using pbsv
                </Typography>
              </Box>
              <Switch
                checked={runSv}
                onChange={(e) => onRunSvChange && onRunSvChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="default"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.success.main,
                    '& + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                  },
                }}
              />
            </Box>
            {(() => {
              const modEnabled = methylationAvailable === 'methylation_ready'
              const modDisabled = !modEnabled
              return (
                <Box
                  onClick={() => modEnabled && onRunModChange && onRunModChange(!runMod)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${
                      runMod && modEnabled ? alpha(theme.palette.success.main, 0.38) : theme.palette.divider
                    }`,
                    backgroundColor: runMod && modEnabled ? alpha(theme.palette.success.main, 0.03) : 'transparent',
                    cursor: modEnabled ? 'pointer' : 'default',
                    opacity: modDisabled ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': modEnabled
                      ? {
                          borderColor: alpha(theme.palette.success.main, 0.25),
                          backgroundColor: alpha(theme.palette.success.main, 0.02),
                        }
                      : {},
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Methylation Analysis (pb-CpG-tools)
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {modDisabled
                        ? 'Methylation data not available for this BAM file.'
                        : 'Detect CpG methylation (5mC) using pb-CpG-tools. Requires MM/ML tags in the BAM file.'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={runMod && modEnabled}
                    onChange={(e) => modEnabled && onRunModChange && onRunModChange(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={modDisabled}
                    color="default"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: theme.palette.success.main,
                        '& + .MuiSwitch-track': { backgroundColor: theme.palette.success.main },
                      },
                    }}
                  />
                </Box>
              )
            })()}
          </Box>
        </Box>
      </motion.div>
    </Grid>
  )

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      {/* Clinical Analysis Type Selection */}
      {!clinicalAnalysisType && renderClinicalAnalysisSelection()}

      {/* Clinical Configuration - Show After Analysis Type is Selected */}
      {clinicalAnalysisType && (
        <Grid container spacing={2.5}>
          {/* Selected Mode Confirmation */}
          <Grid item xs={12}>
            <motion.div variants={childVariants}>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor:
                    clinicalAnalysisType === 'ai-powered'
                      ? `${alpha(theme.palette.primary.main, 0.03)}`
                      : `${alpha(theme.palette.success.main, 0.03)}`,
                  borderRadius: 2,
                  border: `1px solid ${
                    clinicalAnalysisType === 'ai-powered'
                      ? `${alpha(theme.palette.primary.main, 0.19)}`
                      : `${alpha(theme.palette.success.main, 0.19)}`
                  }`,
                  textAlign: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      backgroundColor:
                        clinicalAnalysisType === 'ai-powered' ? theme.palette.primary.main : theme.palette.success.main,
                      color:
                        clinicalAnalysisType === 'ai-powered'
                          ? theme.palette.primary.contrastText
                          : theme.palette.success.contrastText,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow:
                        clinicalAnalysisType === 'ai-powered'
                          ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          : `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                    }}
                  >
                    {clinicalAnalysisType === 'ai-powered' ? (
                      <AIIcon sx={{ fontSize: '1.1rem' }} />
                    ) : (
                      <StandardIcon sx={{ fontSize: '1.1rem' }} />
                    )}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color:
                        clinicalAnalysisType === 'ai-powered' ? theme.palette.primary.main : theme.palette.success.main,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                    }}
                  >
                    {clinicalAnalysisType === 'ai-powered'
                      ? 'AI-Powered Analysis Selected'
                      : 'Standard Clinical Analysis Selected'}
                  </Typography>
                </Box>

                {clinicalAnalysisType === 'standard' ? (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, mb: 1.5, fontSize: '0.85rem' }}
                  >
                    Optional clinical data can be provided to enhance analysis. This information may be used for future
                    reference.
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, mb: 1.5, fontSize: '0.85rem' }}
                  >
                    AI features are enabled. Please provide clinical context below for optimal results.
                  </Typography>
                )}

                {/* Mode Change Option */}
                <Chip
                  label="Change Analysis Type"
                  variant="outlined"
                  size="small"
                  onClick={() => setClinicalAnalysisType('')}
                  sx={{
                    cursor: 'pointer',
                    height: 28,
                    fontSize: '0.75rem',
                    borderColor: theme.palette.divider,
                    '&:hover': {
                      borderColor:
                        clinicalAnalysisType === 'ai-powered' ? theme.palette.primary.main : theme.palette.success.main,
                      backgroundColor:
                        clinicalAnalysisType === 'ai-powered'
                          ? alpha(theme.palette.primary.main, 0.03)
                          : alpha(theme.palette.success.main, 0.03),
                    },
                  }}
                />
              </Box>
            </motion.div>
          </Grid>

          {isOntFlow && renderOntPipelineOptions()}
          {isPacbioFlow && renderPacbioPipelineOptions()}

          <AnimatePresence>
            {clinicalAnalysisType && (
              <>
                <Grid item xs={12} md={6}>
                  <motion.div variants={aiCardVariants} initial="hidden" animate="visible" exit="exit">
                    <Box
                      sx={{
                        p: 2.5,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                        border:
                          clinicalAnalysisType === 'ai-powered' && !clinicalHistory
                            ? `1px solid ${alpha(theme.palette.warning.main, 0.25)}`
                            : `1px solid ${theme.palette.divider}`,
                        minHeight: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                          Clinical History & Context
                        </Typography>
                        {clinicalAnalysisType === 'ai-powered' && (!clinicalHistory || clinicalHistory.trim() === '') && (
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              backgroundColor: `${alpha(theme.palette.warning.main, 0.08)}`,
                              borderRadius: '12px',
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.19)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.warning.main,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                              }}
                            >
                              Required
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block' }}
                      >
                        {clinicalAnalysisType === 'ai-powered'
                          ? 'Provide detailed clinical information to help AI prioritize relevant variants. HPO suggestions will appear as you type.'
                          : 'Optional clinical information for future reference'}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <StyledTextField
                          fullWidth
                          label="Clinical History"
                          value={clinicalHistory}
                          onChange={(e) => onClinicalHistoryChange(e.target.value)}
                          placeholder="Describe patient history, symptoms, family history, and any other relevant clinical details..."
                          variant="outlined"
                          multiline
                          rows={4}
                          sx={{ minHeight: '100px' }}
                        />
                      </Box>
                    </Box>
                  </motion.div>

                  {clinicalAnalysisType === 'ai-powered' && (
                    <motion.div variants={aiCardVariants} initial="hidden" animate="visible" exit="exit">
                      <Box
                        sx={{
                          p: 2.5,
                          mt: 2.5,
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          minHeight: '240px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                          Analysis Guidance
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block' }}
                        >
                          Specific instructions for the AI analysis
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <StyledTextField
                            fullWidth
                            label="User Guidance"
                            value={userGuidance}
                            onChange={(e) => onUserGuidanceChange(e.target.value)}
                            placeholder="Provide specific analysis instructions, focus areas, or any particular genes/pathways of interest..."
                            variant="outlined"
                            multiline
                            rows={4}
                            sx={{ minHeight: '100px' }}
                          />
                        </Box>
                      </Box>
                    </motion.div>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <motion.div variants={aiCardVariants} initial="hidden" animate="visible" exit="exit">
                    <Box
                      sx={{
                        p: 2.5,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                        border:
                          clinicalAnalysisType === 'ai-powered' && (!hpos || !hpos.hpos || hpos.hpos.length === 0)
                            ? `1px solid ${alpha(theme.palette.warning.main, 0.25)}`
                            : `1px solid ${theme.palette.divider}`,
                        minHeight: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                          Phenotype Terms (HPO)
                        </Typography>
                        {clinicalAnalysisType === 'ai-powered' && (!hpos || !hpos.hpos || hpos.hpos.length === 0) && (
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              backgroundColor: `${alpha(theme.palette.warning.main, 0.08)}`,
                              borderRadius: '12px',
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.19)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.warning.main,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                              }}
                            >
                              Required
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {renderHpoSuggestions()}
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, mb: 0.5, display: 'block', fontWeight: 600 }}
                      >
                        Your selected terms:
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <HPOFilterWrapper
                          value={hpos?.hpoTerms || []}
                          onChange={handleHpoChange}
                          arrayOutput={true}
                          label=""
                        />
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>

                {/* AI Features Info - Only for AI */}
                {clinicalAnalysisType === 'ai-powered' && (
                  <Grid item xs={12}>
                    <motion.div variants={aiCardVariants} initial="hidden" animate="visible" exit="exit">
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: `${alpha(theme.palette.primary.main, 0.02)}`,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, color: theme.palette.primary.main, fontWeight: 600 }}
                        >
                          AI-Powered Features
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                          When AI analysis is enabled, the system will provide intelligent variant prioritization,
                          phenotype-driven filtering, automated literature search, and clinical interpretation
                          assistance. The analysis will consider the provided clinical context to deliver more accurate
                          and relevant results.
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                )}
              </>
            )}
          </AnimatePresence>
        </Grid>
      )}

      <Dialog open={graphOpen} onClose={cancelGraph} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Suggested HPO clusters
          </Typography>
          <Tooltip title="Discard changes" arrow>
            <IconButton size="small" onClick={cancelGraph}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: '82vh' }}>
            {/* Diagnosis pane. Editing here re-runs the suggestion pipeline, so the
                graph beside it refreshes automatically as the text changes. */}
            <Box
              sx={{
                width: 340,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Clinical History &amp; Context
                </Typography>
                {loadingHPO && <CircularProgress size={14} />}
              </Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
                Edit the clinical history below. The graph updates as you type.
              </Typography>
              <StyledTextField
                fullWidth
                value={clinicalHistory}
                onChange={(e) => onClinicalHistoryChange(e.target.value)}
                placeholder="Describe patient history, symptoms, family history, and any other relevant clinical details..."
                variant="outlined"
                multiline
                minRows={6}
                maxRows={26}
                // The inner textarea scrolls itself at maxRows; keep overflow off the root
                // so it never clips the outlined border's top edge (the notch sits at -5px).
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Graph pane */}
            <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
              {hpoGraph && hpoGraph.nodes && hpoGraph.nodes.length > 0 ? (
                <HPOClusterGraph
                  graph={hpoGraph}
                  onAccept={acceptHpoById}
                  onReject={rejectHpoById}
                  onRemove={removeHpoById}
                  onRestore={restoreHpoById}
                  selectedIds={selectedGraphIds}
                  rejectedIds={rejectedGraphIds}
                  height="100%"
                />
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Typography variant="body2">
                    {loadingHPO ? 'Analyzing clinical history…' : 'No phenotype suggestions for the current text.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        {/* Cancel / Save footer. The ✓/✗ node actions apply live but provisionally:
            Cancel (and X / backdrop / Esc) restores the snapshot taken on open, while
            Save keeps the edits. The running count shows exactly what Save will carry
            back into the analysis. */}
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {selectedCount > 0
              ? `${selectedCount} phenotype term${selectedCount === 1 ? '' : 's'} will be added to your analysis`
              : 'No terms selected yet. Click ✓ on the terms you want to include.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={cancelGraph} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={saveGraph}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save &amp; close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </motion.div>
  )
}

export default StepClinicalData
